import classNames from 'classnames'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TooltipProps } from 'recharts/types/component/Tooltip'

import { PerformanceType } from '@pages/IntroducingBroker/Dashboard/QuickPerformance/TypeFilter'

import { Body2, Caption1 } from '@ui/Typography/Typography'

import { getCurrencySymbol } from '@utils/transaction.utils'

import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import styles from './CustomChartTooltip.module.scss'

interface CustomChartTooltipProps extends TooltipProps<number, string> {
  performanceType?: PerformanceType
  labelFormatter?: (label: string) => string
  unit?: CurrencyType
}

export const CustomChartTooltip: React.FC<CustomChartTooltipProps> = ({
  active,
  payload,
  label,
  performanceType,
  labelFormatter,
  unit,
}) => {
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const displayLabel = labelFormatter && label ? labelFormatter(label) : label

  const value =
    payload?.[0]?.value == null ? '-' : formatNumber(payload?.[0].value, { minDecimalPlaces: 0 })

  const text = useMemo<string>(() => {
    switch (performanceType) {
      case PerformanceType.Commission:
        return `${t('IB.Dashboard.Commission')}: ${getCurrencySymbol('EUR')}${value}`
      case PerformanceType.Lots:
        return `${t('IB.Dashboard.Lots')}: ${value}`
      case PerformanceType.Clients:
        return `${t('IB.Dashboard.Clients')}: ${value}`
      default:
        return ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performanceType, t, payload])

  if (active && payload && payload.length) {
    return (
      <div className={styles.customTooltip}>
        <Body2 className={styles.label}>{displayLabel}</Body2>
        <div className={styles.valueWrapper}>
          <div
            className={classNames(
              styles.circle,
              performanceType === PerformanceType.Commission && styles.yellow,
              performanceType === PerformanceType.Lots && styles.green,
              performanceType === PerformanceType.Clients && styles.blue
            )}
          ></div>
          <Caption1>
            {performanceType ? text : `${unit ? getCurrencySymbol(unit) : ''}${value}`}
          </Caption1>
        </div>
      </div>
    )
  }

  return null
}
